// @flow

import { useState, useEffect, useRef } from 'react';
import { type PaymentSpec } from '@tectonics/prs/payment-spec';
import { type PrsSupport } from '@tectonics/prs/support';
import { type UserInputValues } from '@tectonics/prs/user-input-values';
import { type UserInputValidationErrors, type UserInputValidator } from '@tectonics/prs/validation';
import { type IncomingPayment } from '@tectonics/prs/incoming-payment';

type Args = {|
  +spec: PaymentSpec,
  +prsSupport: PrsSupport,
  +userInputValues: UserInputValues,
  +validateUserInput: UserInputValidator,
  +incomingPayment: IncomingPayment,
|};

export function useUserInputValidationErrors({
  spec,
  prsSupport,
  userInputValues,
  validateUserInput,
  incomingPayment,
}: Args): [UserInputValidationErrors, (UserInputValidationErrors => UserInputValidationErrors) => void, bool] {
  const isFirstTimeValidating = useRef(true);

  const [
    userInputValidationErrors,
    setUserInputValidationErrors,
  ] = useState<UserInputValidationErrors>({});

  useEffect(() => {
    isFirstTimeValidating.current = false;

    const newUserInputValidationErrors = Object.entries(userInputValues).reduce((current, [userInputName, userInputValue]) => {
      return {
        ...current,
        [userInputName]: validateUserInput({ spec, prsSupport, userInputName, userInputValue, userInputValues, incomingPayment }),
      };
    }, userInputValidationErrors);
    setUserInputValidationErrors(newUserInputValidationErrors);
  }, [spec, prsSupport, JSON.stringify(userInputValues), validateUserInput]);

  return [
    userInputValidationErrors,
    setUserInputValidationErrors,
    isFirstTimeValidating.current,
  ];
}
